import React, { useState, useEffect, useRef } from 'react';

interface ClientTranscript {
  id: string;
  client_id: string;
  transcript_id: string;
  user_id: string;
  relationship_type: string;
  notes?: string;
  created_at: string;
  // Client details
  client_first_name: string;
  client_last_name: string;
  client_email?: string;
  client_phone_primary: string;
  client_company_name?: string;
  client_status: string;
}

interface AvailableClient {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone_primary: string;
  company_name?: string;
  client_status: string;
}

interface ClientConnectionsProps {
  transcriptId: string;
}

const ClientConnections: React.FC<ClientConnectionsProps> = ({ transcriptId }) => {
  const [connectedClients, setConnectedClients] = useState<ClientTranscript[]>([]);
  const [availableClients, setAvailableClients] = useState<AvailableClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [relationshipType, setRelationshipType] = useState('meeting');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // New state for searchable client selection
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<AvailableClient | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  // Refs for keyboard navigation
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (transcriptId) {
      fetchConnectedClients();
    }
  }, [transcriptId]);

  // Filter clients based on search query
  const filteredClients = availableClients.filter(client => {
    const searchTerm = searchQuery.toLowerCase();
    const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
    const email = (client.email || '').toLowerCase();
    const company = (client.company_name || '').toLowerCase();
    
    return fullName.includes(searchTerm) || 
           email.includes(searchTerm) || 
           company.includes(searchTerm);
  });

  // Reset highlighted index when filtered clients change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredClients]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  // Handle keyboard navigation in client dropdown
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || filteredClients.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredClients.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredClients.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          selectClient(filteredClients[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Select a client from the dropdown
  const selectClient = (client: AvailableClient) => {
    setSelectedClient(client);
    setSelectedClientId(client.id);
    setSearchQuery(`${client.first_name} ${client.last_name}`);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  // Clear client selection
  const clearClientSelection = () => {
    setSelectedClient(null);
    setSelectedClientId('');
    setSearchQuery('');
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const fetchConnectedClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/client-transcripts/list?transcriptId=${transcriptId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server returned non-JSON response. Please check your authentication.');
        }

        const data = await response.json() as {
          success: boolean;
          clientTranscripts: ClientTranscript[];
          error?: string;
        };
        
        if (data.success) {
          setConnectedClients(data.clientTranscripts);
        } else {
          throw new Error(data.error || 'Failed to load connected clients');
        }
      } else if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching connected clients:', err);
      if (err instanceof SyntaxError && err.message.includes('Unexpected token')) {
        setError('Authentication error. Please refresh the page and try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load connected clients');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableClients = async () => {
    try {
      const response = await fetch(`/api/client-transcripts/available-clients?transcriptId=${transcriptId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server returned non-JSON response. Please check your authentication.');
        }

        const data = await response.json() as {
          success: boolean;
          clients: AvailableClient[];
          error?: string;
        };
        
        if (data.success) {
          setAvailableClients(data.clients);
        } else {
          throw new Error(data.error || 'Failed to load available clients');
        }
      } else if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching available clients:', err);
      if (err instanceof SyntaxError && err.message.includes('Unexpected token')) {
        setError('Authentication error. Please refresh the page and try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load available clients');
      }
    }
  };

  const handleAddClient = async () => {
    if (!selectedClientId) {
      setError('Please select a client');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch('/api/client-transcripts/attach', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: selectedClientId,
          transcriptId,
          relationshipType,
          notes: notes.trim() || undefined
        })
      });

      if (response.ok) {
        const data = await response.json() as { success: boolean; error?: string };
        if (data.success) {
          setShowAddModal(false);
          setSelectedClientId('');
          setRelationshipType('meeting');
          setNotes('');
          await fetchConnectedClients(); // Refresh the list
        } else {
          throw new Error(data.error || 'Failed to connect client');
        }
      } else {
        throw new Error('Failed to connect client');
      }
    } catch (err) {
      console.error('Error connecting client:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveClient = async (relationshipId: string) => {
    if (!confirm('Are you sure you want to disconnect this client from the transcript?')) {
      return;
    }

    try {
      const response = await fetch('/api/client-transcripts/detach', {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ relationshipId })
      });

      if (response.ok) {
        const data = await response.json() as { success: boolean; error?: string };
        if (data.success) {
          await fetchConnectedClients(); // Refresh the list
        } else {
          throw new Error(data.error || 'Failed to disconnect client');
        }
      } else {
        throw new Error('Failed to disconnect client');
      }
    } catch (err) {
      console.error('Error disconnecting client:', err);
      setError(err instanceof Error ? err.message : 'Failed to disconnect client');
    }
  };

  const openAddModal = async () => {
    setShowAddModal(true);
    await fetchAvailableClients();
  };

  // Reset states when modal opens
  const resetModalStates = () => {
    setSelectedClientId('');
    setSelectedClient(null);
    setSearchQuery('');
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    setRelationshipType('meeting');
    setNotes('');
    setError(null);
  };

  const openAddModalWithReset = async () => {
    resetModalStates();
    setShowAddModal(true);
    await fetchAvailableClients();
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          👥 Connected Clients
        </h4>
        <button
          onClick={openAddModalWithReset}
          className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded transition-colors"
        >
          + Add Client
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-xs text-red-600 mb-2 p-2 bg-red-50 rounded border border-red-200">
          {error}
        </div>
      )}

      {/* Connected Clients List */}
      {connectedClients.length > 0 ? (
        <div className="space-y-2">
          {connectedClients.map((connection) => (
            <div key={connection.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {connection.client_first_name} {connection.client_last_name}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {connection.client_company_name && (
                    <span>{connection.client_company_name} • </span>
                  )}
                  <span className="capitalize">{connection.relationship_type}</span>
                  {connection.notes && (
                    <span> • {connection.notes}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleRemoveClient(connection.id)}
                className="text-xs text-red-600 hover:text-red-800 ml-2"
                title="Disconnect client"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-400 italic">No clients connected</div>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Connect Client</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Client Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Client
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsDropdownOpen(true)}
                    onBlur={() => {
                      setTimeout(() => setIsDropdownOpen(false), 300);
                    }}
                    onKeyDown={handleKeyDown}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search for a client..."
                    ref={inputRef}
                  />
                  
                  {/* Clear button when client is selected */}
                  {selectedClient && (
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        clearClientSelection();
                      }}
                      className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                      type="button"
                    >
                      ✕
                    </button>
                  )}
                  
                  {/* Dropdown with filtered clients */}
                  {isDropdownOpen && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto" ref={dropdownRef}>
                      {filteredClients.map((client, index) => (
                        <div
                          key={client.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            selectClient(client);
                          }}
                          className={`p-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-blue-50 ${
                            index === highlightedIndex ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="font-medium text-gray-900">
                            {client.first_name} {client.last_name}
                          </div>
                          <div className="text-sm text-gray-500 space-y-1">
                            {client.company_name && (
                              <div>🏢 {client.company_name}</div>
                            )}
                            {client.email && (
                              <div>📧 {client.email}</div>
                            )}
                            <div>📞 {client.phone_primary}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* No results message */}
                  {isDropdownOpen && searchQuery && filteredClients.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-center text-gray-500">
                      No clients found matching "{searchQuery}"
                    </div>
                  )}
                </div>
                
                {/* Selected client display */}
                {selectedClient && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-blue-900">
                          {selectedClient.first_name} {selectedClient.last_name}
                        </div>
                        <div className="text-sm text-blue-700">
                          {selectedClient.company_name && `${selectedClient.company_name} • `}
                          {selectedClient.email}
                        </div>
                      </div>
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          clearClientSelection();
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        type="button"
                        title="Clear selection"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Relationship Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship Type
                </label>
                <select
                  value={relationshipType}
                  onChange={(e) => setRelationshipType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="meeting">Meeting</option>
                  <option value="consultation">Consultation</option>
                  <option value="interview">Interview</option>
                  <option value="call">Phone Call</option>
                  <option value="presentation">Presentation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any additional notes about this connection..."
                />
              </div>

              {/* Error in Modal */}
              {error && (
                <div className="text-sm text-red-600 p-2 bg-red-50 rounded border border-red-200">
                  {error}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddClient}
                disabled={submitting || !selectedClientId}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Connecting...' : 'Connect Client'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientConnections; 